/**
 * FacilitiesAdmin.tsx — Admin > Facilities.
 *
 * The corporate dashboard's Facilities page (components/facilities-table.tsx)
 * for the signed-in account: corp's facility rows without the account
 * grouping above them, laid out like every Admin list (Lead Sources is the
 * reference — see components/admin/AdminList.tsx):
 *   - columns: Facility Name (image, #id), Location, Contact, Type and a
 *     coloured Status pill; Facility Name and Location (by city) sort;
 *   - corp's toolbar: search by name, #id, city, state, address or phone, and
 *     Show deleted; "Add facility" top right; every facility listed, with the
 *     count in the footer;
 *   - clicking a live row opens its edit form; Edit / Delete icons on a live
 *     row, Restore on a deleted one; corp's delete warning first.
 *
 * Reads and writes go through /v5/facilities, the endpoints corp uses, so
 * every field corp edits can be edited here (FacilityFormModal). Delete is a
 * soft delete (active = 0).
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live facility still opens, as a viewer (FacilityFormModal).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, MapPin, Phone, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  FACILITY_STATUS_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  deleteFacility,
  listFacilityRecords,
  restoreFacility,
  type FacilityRecord,
} from '@/services/facilities.service';
import { toast } from '@/store/toastStore';
import { FACILITY_STATUS_CLASS, FacilityFormModal } from './FacilityFormModal';

type ModalState = { kind: 'new' } | { kind: 'edit'; facility: FacilityRecord } | null;

function facilityName(f: FacilityRecord): string {
  return f.name.trim() || `Facility #${f.facilityID}`;
}

function compareByName(a: FacilityRecord, b: FacilityRecord): number {
  return compareText(facilityName(a), facilityName(b)) || a.facilityID - b.facilityID;
}

const SORT_COMPARE: ListSortCompare<FacilityRecord> = {
  name: compareByName,
  location: (a, b) => compareText(a.city, b.city),
};

// Corp's facility columns, in corp order (its image column sits in the name cell).
const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Facility Name', sortable: true },
  { key: 'location', header: 'Location', sortable: true },
  { key: 'contact', header: 'Contact' },
  { key: 'type', header: 'Type', className: 'w-28' },
  { key: 'status', header: 'Status', className: 'w-32' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function matchesSearch(f: FacilityRecord, q: string): boolean {
  if (q === '') return true;
  return (
    String(f.facilityID).includes(q) ||
    [f.name, f.city, f.state, f.address2, f.address, f.phone].some((v) => v.toLowerCase().includes(q))
  );
}

export function FacilitiesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [facilities, setFacilities] = useState<FacilityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [deleting, setDeleting] = useState<FacilityRecord | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setFacilities(await listFacilityRecords());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load facilities'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () => facilities.filter((f) => (showDeleted || f.active) && matchesSearch(f, query)).sort(compareByName),
    [facilities, showDeleted, query],
  );

  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  function handleSaved() {
    setModal(null);
    void load(true);
  }

  function handleImageChanged(id: number, url: string) {
    setFacilities((prev) => prev.map((f) => (f.facilityID === id ? { ...f, facilityImage: url } : f)));
  }

  async function handleDelete() {
    if (!deleting) return;
    const { facilityID } = deleting;
    const name = facilityName(deleting);
    try {
      await deleteFacility(facilityID);
      setFacilities((prev) => prev.map((f) => (f.facilityID === facilityID ? { ...f, active: false } : f)));
      toast.success(`${name} has been deleted.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete facility.'));
    }
  }

  async function handleRestore(f: FacilityRecord) {
    try {
      await restoreFacility(f.facilityID);
      setFacilities((prev) => prev.map((x) => (x.facilityID === f.facilityID ? { ...x, active: true } : x)));
      toast.success(`${facilityName(f)} has been restored.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to restore facility.'));
    }
  }

  return (
    <AdminListPage
      title="Facilities"
      subtitle="Manage your facility locations"
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModal({ kind: 'new' })}>
          Add facility
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search facilities..."
          searchLabel="Search facilities"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="facilities-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading facilities…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No facilities found' : 'No facilities yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a facility to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Facilities" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((f) => {
              const id = f.facilityID;
              const name = facilityName(f);
              const deleted = !f.active;
              const cityLine = [[f.city, f.state].filter(Boolean).join(', '), f.postalCode].filter(Boolean).join(' ');
              return (
                <tr
                  key={id}
                  data-facility-id={id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  // A live row opens its form; a deleted one is restored, not edited.
                  onClick={deleted ? undefined : () => setModal({ kind: 'edit', facility: f })}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
                        {f.facilityImage ? (
                          <img src={f.facilityImage} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Building2 className="h-5 w-5 text-slate-400" aria-hidden />
                        )}
                      </div>
                      <AdminRowName
                        deleted={deleted}
                        nameTestId="facility-name"
                        suffix={<span className="font-mono text-[11px] text-slate-400">#{id}</span>}
                      >
                        {name}
                      </AdminRowName>
                    </div>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {(f.address2 || cityLine) && (
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                        <div className="text-slate-600">
                          {f.address2 && <div>{f.address2}</div>}
                          {cityLine && <div>{cityLine}</div>}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {f.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-slate-400" aria-hidden />
                        {f.phone}
                      </div>
                    )}
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-600')}>
                    {FACILITY_TYPE_OPTIONS.find((o) => o.value === f.facilityType)?.label ?? '—'}
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                        FACILITY_STATUS_CLASS[f.facilityStatus],
                      )}
                    >
                      {FACILITY_STATUS_OPTIONS.find((o) => o.value === f.facilityStatus)?.label ?? f.facilityStatus}
                    </span>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(f)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setModal({ kind: 'edit', facility: f })} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(f)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['facility', 'facilities']} />
        </>
      )}

      {modal && (
        <FacilityFormModal
          key={modal.kind === 'edit' ? `edit-${modal.facility.facilityID}` : 'new'}
          facility={modal.kind === 'edit' ? modal.facility : null}
          facilities={facilities}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onImageChanged={handleImageChanged}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Facility"
        message={
          deleting ? (
            <span className="space-y-2">
              <span className="block">
                Are you sure you want to delete <span className="font-medium text-slate-900">{facilityName(deleting)}</span>?
              </span>
              <span className="block font-medium text-red-600">
                Deleting a facility could interfere with active operations, scheduling, and reporting.
              </span>
              <span className="block text-slate-500">This action can be undone by restoring the facility from the table.</span>
            </span>
          ) : (
            ''
          )
        }
        confirmLabel="Delete Facility"
        variant="danger"
      />
    </AdminListPage>
  );
}
